import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Icons } from 'copyright-clearance-center-common-ui';
import { useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		scrollContainder: {
			overflowX: 'hidden',
			height: '2rem',
			'& nav': {
				display: 'inline-block',
				whiteSpace: 'nowrap'
			}
		},
		tabs: {
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			flex: '1 0 0'
		}
	})
);

export interface TabItem {
	title?: string;
	iconSvg?: string;
	tabIndex: number;
}

export interface ITabsProps {
	children?: React.ReactNode;
	small?: boolean;
	headerClassName?: string;
	onChange: (tabIndex: number) => void;
	tabIndex: number;
	items: TabItem[];
}

const Tabs: React.FunctionComponent<ITabsProps> = props => {
	const classes = useStyles();
	const containerRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const scrollLeft = useRef(0);
	const [showLeftButton, setShowLeftButton] = useState(true);
	const [showRightButton, setShowRightButton] = useState(true);

	const scrollToTab = (index: number) => {
		if (containerRef.current) {
			const selectedTabElement = containerRef.current.querySelector(`[tabindex="${index}"]`);
			if (selectedTabElement) {
				selectedTabElement.scrollIntoView({ behavior: 'smooth' });

				setTimeout(() => {
					selectedTabElement.scrollIntoView({ behavior: 'smooth' });
				}, 100);
			}
		}
	};

	const handleTabClick = (index: number) => {
		props.onChange(index);
		scrollToTab(index);
	};

	const handleOnScroll = () => {
		if (containerRef.current) {
			const containerWidth = containerRef.current.offsetWidth;
			const maxScrollLeft = containerRef.current.scrollWidth - containerWidth;
			const currentScrollLeft = containerRef.current.scrollLeft;
			const scrollButtonWidth = (containerRef.current.parentElement.offsetWidth - containerWidth) / 2;

			setShowLeftButton(currentScrollLeft > 0);
			setShowRightButton(currentScrollLeft < maxScrollLeft - scrollButtonWidth);
			scrollLeft.current = containerRef.current.scrollLeft;
		}
	};

	useEffect(() => {
		scrollToTab(props.tabIndex);
		handleOnScroll();
	}, [props.tabIndex]);

	useEffect(() => {
		const observer = new ResizeObserver(() => {
			handleOnScroll();
		});

		observer.observe(headerRef.current);
		return () => observer.disconnect();
	}, []);

	const handleScrollButtonClick = (left: boolean) => {
		if (containerRef.current) {
			const scrollAmount = Math.ceil(containerRef.current.parentElement.offsetWidth * 0.8);
			let newScrollLeft;

			if (left) {
				const mostLeftPosition = 0;
				newScrollLeft = Math.max(scrollLeft.current - scrollAmount, mostLeftPosition);
			} else {
				const mostRightPosition = containerRef.current.scrollWidth - containerRef.current.offsetWidth;
				newScrollLeft = Math.min(scrollLeft.current + scrollAmount, mostRightPosition);
			}

			containerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
		}
	};

	return (
		<div className={clsx(classes.tabs, 'rup-tabs', { ['rup-tabs--sm']: props.small })}>
			<div ref={headerRef} className={props.headerClassName}>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{showLeftButton && (
						<button
							className="rup-icon-btn rup-icon-btn--neutral rup-icon-btn--sm"
							type="button"
							data-test-id="scroll-btn-left"
							onClick={() => handleScrollButtonClick(true)}
							style={{ minWidth: '1.5rem' }}
						>
							<span dangerouslySetInnerHTML={{ __html: Icons.ChevronLeftIcon }} />
						</button>
					)}
					<div className={classes.scrollContainder} ref={containerRef} onScroll={handleOnScroll}>
						<nav role="tablist" className="rup-tabs__list">
							{props.items.map(tab => (
								<button
									type="button"
									role="tab"
									className="rup-tabs__nav"
									tabIndex={tab.tabIndex}
									key={tab.tabIndex}
									aria-selected={tab.tabIndex === props.tabIndex}
									onClick={() => handleTabClick(tab.tabIndex)}
								>
									{tab.iconSvg && <span dangerouslySetInnerHTML={{ __html: tab.iconSvg }} />}
									{tab.title}
								</button>
							))}
						</nav>
					</div>
					{showRightButton && (
						<button
							className="rup-icon-btn rup-icon-btn--neutral rup-icon-btn--sm"
							type="button"
							data-test-id="scroll-btn-right"
							onClick={() => handleScrollButtonClick(false)}
							style={{ minWidth: '1.5rem' }}
						>
							<span dangerouslySetInnerHTML={{ __html: Icons.ChevronRightIcon }} />
						</button>
					)}
				</div>
			</div>
			{props.children}
		</div>
	);
};

export default Tabs;
